import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  Tooltip,
  Stack,
  CircularProgress,
  useMediaQuery,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  Assignment as AssignmentIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { enqueueSnackbar } from 'notistack';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import templateService, { Template } from '../services/templateService';
import TemplateForm from '../components/forms/TemplateForm';
import TemplateContratoForm from '../components/forms/TemplateContratoForm';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 1.5 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const TemplatesPage: React.FC = () => {
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tabValue, setTabValue] = useState(0);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);

  const templateTypes = [
    { value: 'contrato', label: 'Contratos', icon: <DescriptionIcon />, color: theme.palette.primary.main },
    { value: 'email', label: 'E-mails', icon: <EmailIcon />, color: '#1976d2' },
    { value: 'sms', label: 'SMS', icon: <SmsIcon />, color: '#2e7d32' },
    { value: 'termos', label: 'Termos', icon: <AssignmentIcon />, color: '#7b1fa2' },
    { value: 'recibo', label: 'Recibos', icon: <ReceiptIcon />, color: '#f57c00' },
  ];

  const currentType = templateTypes[tabValue]?.value;

  useEffect(() => {
    loadTemplates();
  }, [tabValue, page, rowsPerPage]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await templateService.getTemplates({
        type: currentType,
        page: page + 1,
        limit: rowsPerPage,
      });

      console.log('✅ Resposta recebida:', response);

      setTemplates(response.templates);
      setTotalItems(response.totalItems);
    } catch (error: any) {
      console.error('❌ Erro ao carregar templates:', error);
      console.error('❌ Detalhes do erro:', error.response?.data || error.message);
      enqueueSnackbar('Erro ao carregar templates', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  const handleNewTemplate = () => {
    setSelectedTemplate(null);
    setFormOpen(true);
  };

  const handleEditTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setFormOpen(true);
  };

  const handleDeleteTemplate = async (template: Template) => {
    if (!window.confirm(`Tem certeza que deseja excluir o template "${template.title}"?`)) {
      return;
    }

    try {
      await templateService.deleteTemplate(template.id);
      enqueueSnackbar('Template excluído com sucesso!', { variant: 'success' });
      loadTemplates();
    } catch (error) {
      console.error('Erro ao excluir template:', error);
      enqueueSnackbar('Erro ao excluir template', { variant: 'error' });
    }
  };

  const handleToggleStatus = async (template: Template) => {
    try {
      await templateService.toggleTemplateStatus(template.id);
      enqueueSnackbar(
        `Template ${template.isActive ? 'desativado' : 'ativado'} com sucesso!`,
        { variant: 'success' }
      );
      loadTemplates();
    } catch (error) {
      console.error('Erro ao alterar status do template:', error);
      enqueueSnackbar('Erro ao alterar status do template', { variant: 'error' });
    }
  };

  const handleFormSuccess = () => {
    loadTemplates();
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const getStatusChip = (isActive: boolean) => (
    <Chip
      label={isActive ? 'Ativo' : 'Inativo'}
      color={isActive ? 'success' : 'default'}
      size="small"
      sx={{ height: 20, fontSize: '0.7rem' }}
    />
  );

  const renderTable = () => (
    <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader sx={{ '& .MuiTableBody-root .MuiTableCell-root': { py: 0.5, fontSize: '0.78rem' } }}>
          <TableHead>
            <TableRow sx={{
              '& .MuiTableCell-head': {
                bgcolor: (t) => `color-mix(in srgb, ${primario} 12%, ${t.palette.background.paper})`,
                fontWeight: 700,
                fontSize: '0.73rem',
                py: 0.8,
                borderBottom: `2px solid ${alpha(primario, 0.2)}`,
              },
            }}>
              <TableCell>Título</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell>Atualizado em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : templates.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                  Nenhum template encontrado
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            templates.map((template) => (
              <TableRow key={template.id} hover sx={{ '&:hover': { bgcolor: alpha(primario, 0.03) } }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {template.title}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {getStatusChip(template.isActive)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {template.createdAt && formatDate(template.createdAt)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {template.updatedAt && formatDate(template.updatedAt)}
                </TableCell>
                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                        sx={{ p: 0.5 }}
                      >
                        <EditIcon sx={{ fontSize: '1rem' }} />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={template.isActive ? 'Desativar' : 'Ativar'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleStatus(template)}
                        sx={{ p: 0.5 }}
                      >
                        {template.isActive ? <VisibilityOffIcon sx={{ fontSize: '1rem' }} /> : <VisibilityIcon sx={{ fontSize: '1rem' }} />}
                      </IconButton>
                    </Tooltip>
                    
                    {!template.title.includes('Padrão') && (
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteTemplate(template)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação - dentro do Paper como nas outras páginas */}
      {totalItems > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.5,
            px: 2,
            bgcolor: alpha(primario, 0.04),
            borderTop: `2px solid ${alpha(primario, 0.2)}`,
          }}
        >
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
            sx={{
              '.MuiTablePagination-toolbar': {
                minHeight: 34,
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 2,
                '& .MuiTablePagination-spacer': { display: 'none' },
              },
              '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.875rem' },
              '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.875rem' },
              '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.875rem' },
              '.MuiTablePagination-actions': { ml: 0.5 },
            }}
          />
        </Box>
      )}
    </Paper>
  );

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(primario, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
        {/* Cabeçalho */}
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(primario, 0.12), color: primario, width: 40, height: 40 }}>
              <DescriptionIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Templates
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Gerencie templates para contratos, e-mails, SMS, termos de uso e recibos
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewTemplate}
            size="small"
            fullWidth={isMobile}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.78rem', borderRadius: 1.5 }}
          >
            Novo Template
          </Button>
        </Box>

        {/* Abas */}
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 42, gap: 0.8 },
          }}
        >
          {templateTypes.map((type, index) => (
            <Tab
              key={type.value}
              icon={React.cloneElement(type.icon, {
                sx: { fontSize: 17, color: tabValue === index ? type.color : 'text.secondary' },
              })}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  {type.label}
                  {tabValue === index && (
                    <Chip
                      label={totalItems}
                      size="small"
                      sx={{
                        fontSize: '0.65rem',
                        height: 18,
                        bgcolor: alpha(type.color, 0.12),
                        color: type.color,
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Conteúdo das abas */}
      {templateTypes.map((type, index) => (
        <TabPanel key={type.value} value={tabValue} index={index}>
          {renderTable()}
        </TabPanel>
      ))}

      {/* Modal do formulário - TemplateContratoForm para contratos, TemplateForm para demais */}
      {currentType === 'contrato' ? (
          <TemplateContratoForm
            open={formOpen}
            onClose={() => setFormOpen(false)}
            onSuccess={handleFormSuccess}
            template={selectedTemplate}
          />
        ) : (
          <TemplateForm
            open={formOpen}
            onClose={() => setFormOpen(false)}
            onSuccess={handleFormSuccess}
            template={selectedTemplate}
        />
      )}
    </Box>
  );
};

export default TemplatesPage; 